/**
 * Add All Variants to Basket
 * Bulk-adds every purchasable variant via a single /cart/add.js request.
 * Integrates with Flex AJAX cart (drawer / mini cart).
 */
(() => {
  'use strict';

  if (window.PXUThemeAddAllToCart) return;

  const SELECTORS = {
    button: '[data-add-all-to-cart]',
    form: '[data-product-form]',
    quantity: 'input[name="quantity"]',
    variantsJson: '[data-add-all-variants-json]',
    cartWarning: '.cart-warning',
    cartTrigger: '[data-ajax-cart-trigger]',
    label: '[data-add-all-label]',
    status: '[data-add-all-status]',
    feedback: '.ajaxified-cart-feedback',
  };

  const DEFAULTS = {
    loading: 'Adding all variants...',
    empty: 'No available variants to add.',
    error: 'Unable to add variants to basket.',
  };

  const escapeHtml = (value) =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const getCartAddUrl = () => `${window.Shopify?.routes?.root || '/'}cart/add.js`;

  const getLabels = (button) => ({
    idle: button.dataset.label || button.textContent.trim(),
    loading: button.dataset.loadingLabel || DEFAULTS.loading,
    empty: button.dataset.emptyLabel || DEFAULTS.empty,
    error: button.dataset.errorLabel || DEFAULTS.error,
  });

  const parseVariants = (form) => {
    const jsonEl = form.querySelector(SELECTORS.variantsJson);
    if (!jsonEl) return [];

    try {
      const parsed = JSON.parse(jsonEl.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.log('Failed to parse add-all variants JSON', error);
      return [];
    }
  };

  const getQuantity = (form) => {
    const value = Number.parseInt(form.querySelector(SELECTORS.quantity)?.value, 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  /** Cap quantity when inventory is tracked and overselling is denied. */
  const resolveLineQuantity = (variant, quantity) => {
    if (!variant?.id) return 0;

    const { inventory_management: managed, inventory_policy: policy, inventory_quantity: stock } =
      variant;

    if (managed && policy === 'deny' && typeof stock === 'number') {
      return stock > 0 ? Math.min(quantity, stock) : 0;
    }

    return quantity;
  };

  const buildCartItems = (variants, quantity) =>
    variants.flatMap((variant) => {
      const lineQuantity = resolveLineQuantity(variant, quantity);
      return lineQuantity > 0 ? [{ id: variant.id, quantity: lineQuantity }] : [];
    });

  const setButtonLoading = (button, isLoading, label) => {
    button.disabled = isLoading;
    button.setAttribute('aria-busy', String(isLoading));
    button.classList.toggle('is-loading', isLoading);

    const textEl = button.querySelector(SELECTORS.label);
    const statusEl = button.querySelector(SELECTORS.status);

    if (textEl && label != null) textEl.textContent = label;
    if (statusEl) statusEl.textContent = isLoading && label ? label : '';
  };

  const setWarning = (form, message = '') => {
    const warning = form.querySelector(SELECTORS.cartWarning);
    if (!warning) return;

    warning.innerHTML = message
      ? `<p class="cart-warning__message animated bounceIn warning">${escapeHtml(message)}</p>`
      : '';
  };

  const revealCartUi = () => {
    const ajaxCart = window.PXUTheme?.jsAjaxCart;
    ajaxCart?.showDrawer?.();
    ajaxCart?.updateView?.();

    document
      .querySelectorAll(SELECTORS.cartTrigger)
      .forEach((el) => el.classList.add('show-mini-cart'));

    const feedback = document.querySelector(SELECTORS.feedback);
    if (!feedback) return;

    feedback.style.display = '';
    feedback.classList.add('is-visible');
  };

  const getErrorMessage = (body, fallback = DEFAULTS.error) => {
    if (typeof body?.description === 'string' && body.description) {
      return body.description.replace('All 1 ', 'All ');
    }

    return typeof body?.message === 'string' && body.message ? body.message : fallback;
  };

  const addItemsToCart = async (items) => {
    const response = await fetch(getCartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    });

    const body = await response.json();

    if (!response.ok) {
      const error = new Error(getErrorMessage(body));
      error.response = body;
      error.status = response.status;
      throw error;
    }

    return body;
  };

  const handleClick = async (event) => {
    const button = event.target.closest(SELECTORS.button);
    if (!button || button.disabled) return;

    event.preventDefault();

    const form = button.closest(SELECTORS.form);
    if (!form) return;

    const labels = getLabels(button);
    const items = buildCartItems(parseVariants(form), getQuantity(form));

    if (!items.length) {
      setWarning(form, labels.empty);
      return;
    }

    setWarning(form);
    setButtonLoading(button, true, labels.loading);

    try {
      await addItemsToCart(items);
      revealCartUi();
    } catch (error) {
      console.log('Add all to cart failed', error);
      setWarning(form, getErrorMessage(error.response, error.message || labels.error));
    } finally {
      setButtonLoading(button, false, labels.idle);
    }
  };

  document.addEventListener('click', handleClick);

  window.PXUThemeAddAllToCart = { version: '1.1.0' };
})();
